/**
 * PATH: apps/hr/src/pages/settings/SettingsPage.tsx
 * PURPOSE: Main settings page with categorized settings navigation
 * 
 * ROLE IN ARCHITECTURE: Central settings hub for HR configuration
 */

import { Link } from 'react-router-dom'
import {
  FileText,
  Tag,
  Bell,
  Settings,
  Users,
  Shield,
  Mail,
  Building2,
  Sliders,
  ChevronRight,
} from 'lucide-react'

// =============================================================================
// SETTINGS CATEGORIES
// =============================================================================

interface SettingItem {
  id: string
  label: string
  description: string
  icon: React.ElementType
  href: string
  badge?: string
}

interface SettingsCategory {
  id: string
  label: string
  description: string
  items: SettingItem[]
}

const SETTINGS_CATEGORIES: SettingsCategory[] = [
  {
    id: 'documents-ocr',
    label: 'Documents & OCR Settings',
    description: 'Configure document types, OCR extraction, and alert policies',
    items: [
      {
        id: 'document-types',
        label: 'Document Types',
        description: 'Manage document types, OCR fields, and extraction rules',
        icon: FileText,
        href: '/settings/document-types',
      },
      {
        id: 'tags',
        label: 'Tag Management',
        description: 'Create and manage tags for documents and employees',
        icon: Tag,
        href: '/settings/tags',
      },
      {
        id: 'alert-policies',
        label: 'Alert Policies',
        description: 'Configure expiry alerts and notification rules',
        icon: Bell,
        href: '/settings/alert-policies',
      },
    ],
  },
  {
    id: 'organisation',
    label: 'Organisation',
    description: 'Company settings and structure',
    items: [
      {
        id: 'company',
        label: 'Company Profile',
        description: 'Update company name, logo, and contact details',
        icon: Building2,
        href: '/settings/company',
        badge: 'Coming Soon',
      },
      {
        id: 'departments',
        label: 'Departments & Teams',
        description: 'Manage organisational structure',
        icon: Users,
        href: '/settings/departments',
        badge: 'Coming Soon',
      },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance & Security',
    description: 'Security settings and compliance configuration',
    items: [
      {
        id: 'compliance-rules',
        label: 'Compliance Rules',
        description: 'Configure document requirements by role',
        icon: Shield,
        href: '/settings/compliance-rules',
        badge: 'Coming Soon',
      },
      {
        id: 'audit-log',
        label: 'Audit Log',
        description: 'View system activity and changes',
        icon: Sliders,
        href: '/settings/audit-log',
        badge: 'Coming Soon',
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'Connect external services',
    items: [
      {
        id: 'email',
        label: 'Email Settings',
        description: 'Configure email notifications and templates',
        icon: Mail,
        href: '/settings/email',
        badge: 'Coming Soon',
      },
    ],
  },
]

// =============================================================================
// SETTING ITEM CARD
// =============================================================================

function SettingItemCard({ item }: { item: SettingItem }) {
  const Icon = item.icon
  const isDisabled = !!item.badge

  const content = (
    <div className={`
      flex items-center gap-4 p-4 rounded-xl border transition-all
      ${isDisabled 
        ? 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 opacity-60 cursor-not-allowed' 
        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm cursor-pointer'
      }
    `}>
      <div className={`p-3 rounded-xl ${isDisabled ? 'bg-slate-100 dark:bg-slate-800' : 'bg-teal-50 dark:bg-teal-900/30'}`}>
        <Icon className={`w-5 h-5 ${isDisabled ? 'text-slate-400' : 'text-teal-600 dark:text-teal-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900 dark:text-white">{item.label}</p>
          {item.badge && (
            <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-full">
              {item.badge}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{item.description}</p>
      </div>
      {!isDisabled && (
        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </div>
  )

  if (isDisabled) {
    return content
  }

  return (
    <Link to={item.href}>
      {content}
    </Link>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function SettingsPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
          Settings
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Configure HR Hub settings and preferences
        </p>
      </div>

      {/* Settings Categories */}
      {SETTINGS_CATEGORIES.map((category) => (
        <div key={category.id} className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {category.label}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {category.description}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {category.items.map((item) => (
              <SettingItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default SettingsPage
